import type { InferContractRouterInputs } from '@orpc/contract'
import type { Abi, Address, Hex } from 'viem'

import {
  type ActionFlow,
  ActionFlowError,
  type CallRequest,
  createImmutableActionFlow,
  type ResolvedCall,
  type SignatureRequest,
  waitForConfirmations
} from '@repo/web3'

import type { Contract } from '@/lib/orpc/contracts'

import { orpc } from '@/lib/orpc/client'

type ShadowActionInput = InferContractRouterInputs<Contract>['shared']['shadowAction']

/**
 * A transaction as its builder hands it over, whichever builder that is (morpho-sdk today, the
 * Earn & Borrow API later). The flow collects `signatures`, sends `requirements`, then sends the
 * transaction `buildTransaction` returns.
 */
export interface ActionFlowSource {
  /** Off-chain signatures (permits, Morpho authorizations), collected in this order. */
  readonly signatures: readonly SignatureRequest[]
  /** On-chain prerequisites (ERC-20 approvals, Morpho `setAuthorization`), sent in this order. */
  readonly requirements: readonly ActionFlowSourceCall[]
  /** Uses only passed signatures and no per-flow state; may resolve async after the last signature, before calls are sent. */
  readonly buildTransaction: (signatures: readonly Hex[]) => ResolvedCall | Promise<ResolvedCall>
}

export interface ActionFlowSourceCall {
  readonly label: string
  readonly call: ResolvedCall
}

export interface VerifyTransactionInput {
  signatures: readonly Hex[]
}

interface BuildActionFlowParameters extends Pick<
  ActionFlow,
  'chainId' | 'label' | 'tag' | 'onSuccess' | 'attribution'
> {
  source: ActionFlowSource
  /** Account the transaction was built for; execution aborts if the signer differs. */
  account: Address
  transactionLabel: string
  /** ABI of the transaction's target contract, used to decode its revert reason. */
  errorAbi: Abi
  /** Whether the wallet supports atomically batching transaction calls. */
  walletSupportsAtomicBatching: boolean
  /** Shadow request metadata; signature kinds are supplied because SignatureRequest carries no kind. */
  shadow?: {
    action: ShadowActionInput['action']
    reviewSessionId: ShadowActionInput['reviewSessionId']
    walletType: ShadowActionInput['walletType']
    signatureKinds: ShadowActionInput['sdk']['signatureKinds']
  }
  /** Runs after the last signature and before any call is sent. A throw stops the flow. */
  verifyTransaction?: (input: VerifyTransactionInput) => Promise<void>
}

export async function buildActionFlow({
  source,
  account,
  transactionLabel,
  errorAbi,
  walletSupportsAtomicBatching,
  shadow,
  verifyTransaction,
  ...flow
}: BuildActionFlowParameters) {
  const collectedSignatures: (Hex | undefined)[] = Array.from(
    { length: source.signatures.length },
    (): Hex | undefined => undefined
  )
  let resolvedCall: ResolvedCall | undefined
  async function verify() {
    try {
      const signatures = collectedSignatures.filter(
        (signature): signature is Hex => signature !== undefined
      )
      if (signatures.length !== source.signatures.length) {
        throw new Error('Cannot verify the transaction before every signature is collected')
      }
      if (shadow && resolvedCall) {
        void orpc.shared.shadowAction
          .call({
            reviewSessionId: shadow.reviewSessionId,
            chainId: flow.chainId,
            walletType: shadow.walletType,
            action: shadow.action,
            sdk: {
              calls: [...source.requirements.map(({ call }) => call), resolvedCall].map(call => ({
                to: call.to,
                data: call.data,
                value: (call.value ?? 0n).toString()
              })),
              signatureKinds: shadow.signatureKinds,
              balanceChanges: null
            }
          })
          .catch(() => {})
      }
      await verifyTransaction?.({ signatures })
    } catch (error) {
      if (error instanceof ActionFlowError) throw error
      throw new ActionFlowError(error instanceof Error ? error.message : String(error), {
        cause: error
      })
    }
  }

  const signatureRequests = source.signatures.map(
    (signatureRequest, slot): SignatureRequest => ({
      label: signatureRequest.label,
      sign: async client => {
        resolvedCall = undefined
        const signature = await signatureRequest.sign(client)
        collectedSignatures[slot] = signature
        const filledSignatures = collectedSignatures.filter(
          (collectedSignature): collectedSignature is Hex => collectedSignature !== undefined
        )
        if (filledSignatures.length === collectedSignatures.length) {
          try {
            resolvedCall = await source.buildTransaction(filledSignatures)
          } catch (error) {
            throw new ActionFlowError('Failed to build the transaction', { cause: error })
          }
        }
        return signature
      }
    })
  )

  if (signatureRequests.length === 0) {
    resolvedCall = await source.buildTransaction([])
  }

  const requirements: CallRequest[] = source.requirements.map(({ label, call }) => {
    const wait = walletSupportsAtomicBatching ? undefined : waitForConfirmations(2)

    return {
      label,
      getCall: () => call,
      ...(wait && { after: wait })
    }
  })

  const transactionCall: CallRequest = {
    label: transactionLabel,
    errorAbi,
    getCall: () => {
      if (resolvedCall === undefined) {
        throw new Error('Cannot build transaction before every signature is collected')
      }
      return resolvedCall
    }
  }
  const [firstCall, ...restCallRequests] = [...requirements, transactionCall]
  if (!firstCall) throw new Error('An action flow requires a transaction call')
  const callRequests: CallRequest[] = [{ ...firstCall, before: verify }, ...restCallRequests]

  return createImmutableActionFlow({
    ...flow,
    expectedAccountAddress: account,
    signatureRequests,
    callRequests
  })
}
